/**
 * Tenant auth store using Zustand
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserResponse, TokenResponse, UserRole } from '@intellimon/shared/types';
import { tenantTokens, isTokenExpired } from '@intellimon/shared/utils';
import { tenantApi } from '../api/client';

interface AuthState {
  user: UserResponse | null;
  tenantSlug: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  initialize: () => Promise<void>;
  login: (slug: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  hasRole: (requiredRoles: UserRole[]) => boolean;
}

const ROLE_HIERARCHY: Record<UserRole, number> = {
  admin: 4,
  management: 3,
  monitor: 2,
  user: 1,
};

const authStoreCreator = persist<AuthState>(
  (set, get) => ({
      user: null,
      tenantSlug: tenantTokens.getTenantSlug(),
      isAuthenticated: false,
      isLoading: true,
      error: null,

      initialize: async () => {
        const token = tenantTokens.getAccessToken();
        const slug = tenantTokens.getTenantSlug();

        if (!token || !slug || isTokenExpired(token)) {
          tenantTokens.clearTokens();
          set({ isAuthenticated: false, user: null, tenantSlug: null, isLoading: false });
          return;
        }

        try {
          const response = await tenantApi.get<UserResponse>('/auth/me');
          set({
            user: response.data,
            tenantSlug: slug,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch {
          tenantTokens.clearTokens();
          set({ isAuthenticated: false, user: null, tenantSlug: null, isLoading: false });
        }
      },

      login: async (slug: string, email: string, password: string) => {
        set({ isLoading: true, error: null });
        tenantTokens.setTenantSlug(slug);

        try {
          const formData = new FormData();
          formData.append('username', email);
          formData.append('password', password);

          const tokenResponse = await tenantApi.post<TokenResponse>(
            '/auth/login',
            formData,
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'X-Tenant-Slug': slug,
              },
            }
          );

          tenantTokens.setAccessToken(tokenResponse.data.access_token);
          tenantTokens.setRefreshToken(tokenResponse.data.refresh_token);

          const userResponse = await tenantApi.get<UserResponse>('/auth/me');
          set({
            user: userResponse.data,
            tenantSlug: slug,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error: unknown) {
          tenantTokens.clearTokens();
          const message =
            error instanceof Error
              ? error.message
              : 'Login failed. Please check your credentials.';
          set({ error: message, isLoading: false, tenantSlug: null });
          throw error;
        }
      },

      logout: () => {
        tenantTokens.clearTokens();
        set({ user: null, tenantSlug: null, isAuthenticated: false, error: null });
      },

      clearError: () => set({ error: null }),

      hasRole: (requiredRoles: UserRole[]) => {
        const { user } = get();
        if (!user) return false;

        const userRoleLevel = ROLE_HIERARCHY[user.role];
        return requiredRoles.some(
          (role) => userRoleLevel >= ROLE_HIERARCHY[role]
        );
      },
    }),
  {
    name: 'tenant-auth',
    partialize: (state) => ({ user: state.user, tenantSlug: state.tenantSlug }) as AuthState,
  }
)

export const useAuthStore = create(authStoreCreator)

// Expose to window for shared components
declare global {
  interface Window {
    __INTELLIMON_AUTH_STORE__?: typeof useAuthStore;
  }
}

if (typeof window !== 'undefined') {
  window.__INTELLIMON_AUTH_STORE__ = useAuthStore
}
